const GDPR_STORAGE_KEY = 'gdpr-consent-v1';
const GA_MEASUREMENT_ID = 'G-D8WLY6BX3D';
const GA_SCRIPT_ID = 'ga-gtag-script';

type ConsentState = 'granted' | 'denied';

export interface GdprConsentPreferences {
  essential: true;
  statistics: boolean;
  analyticsMarketing: boolean;
  updatedAt: string;
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    __gaConfigured?: boolean;
  }
}

function ensureGtagShim(): void {
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }

  if (typeof window.gtag !== 'function') {
    window.gtag = function gtagShim(...args: unknown[]) {
      window.dataLayer.push(args);
    };
  }
}

function toConsentState(enabled: boolean): ConsentState {
  return enabled ? 'granted' : 'denied';
}

function applyConsentToGtag(consent: GdprConsentPreferences): void {
  ensureGtagShim();
  window.gtag('consent', 'update', {
    analytics_storage: toConsentState(consent.statistics),
    ad_storage: toConsentState(consent.analyticsMarketing),
    ad_user_data: toConsentState(consent.analyticsMarketing),
    ad_personalization: toConsentState(consent.analyticsMarketing),
    functionality_storage: 'granted',
    security_storage: 'granted',
  });
}

function applyDefaultDeniedConsent(): void {
  ensureGtagShim();
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
  });
}

function ensureGtagScript(): void {
  if (document.getElementById(GA_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement('script');
  script.id = GA_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

function ensureConfigured(): void {
  if (window.__gaConfigured) {
    return;
  }

  ensureGtagShim();
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID);
  window.__gaConfigured = true;
}

export function readGdprConsent(): GdprConsentPreferences | null {
  const raw = localStorage.getItem(GDPR_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<GdprConsentPreferences>;
    if (typeof parsed.statistics !== 'boolean' || typeof parsed.analyticsMarketing !== 'boolean') {
      return null;
    }

    return {
      essential: true,
      statistics: parsed.statistics,
      analyticsMarketing: parsed.analyticsMarketing,
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function saveGdprConsent(input: {
  statistics: boolean;
  analyticsMarketing: boolean;
}): GdprConsentPreferences {
  const value: GdprConsentPreferences = {
    essential: true,
    statistics: input.statistics,
    analyticsMarketing: input.analyticsMarketing,
    updatedAt: new Date().toISOString(),
  };

  localStorage.setItem(GDPR_STORAGE_KEY, JSON.stringify(value));
  return value;
}

export function hasGdprDecision(): boolean {
  return readGdprConsent() !== null;
}

export function initializeAnalyticsFromConsent(consent: GdprConsentPreferences | null): void {
  applyDefaultDeniedConsent();

  if (!consent) {
    return;
  }

  applyConsentToGtag(consent);

  if (!consent.statistics && !consent.analyticsMarketing) {
    return;
  }

  ensureGtagScript();
  ensureConfigured();
}

export function applyConsentSelection(consent: GdprConsentPreferences): void {
  applyConsentToGtag(consent);

  if (!consent.statistics && !consent.analyticsMarketing) {
    return;
  }

  ensureGtagScript();
  ensureConfigured();
}
